import { ORG_PERSON_CARD_SPECS } from './orgChartShapeVariants';
import type { OrgPersonCardViewMode } from './orgChartShapeVariants';

const PHOTO_CENTER_AVATAR_SIZE = ORG_PERSON_CARD_SPECS.compact.image.top_center.size;
export const ORG_PHOTO_CENTER_AVATAR_OUTER_RADIUS = (PHOTO_CENTER_AVATAR_SIZE + 10) / 2;
const ORG_PHOTO_SIDE_FULL_CENTER_Y = -6;
const ORG_PHOTO_SIDE_COMPACT_CENTER_X_OFFSET = 3;
const ORG_PHOTO_SIDE_FULL_CENTER_X_INSET = PHOTO_CENTER_AVATAR_SIZE / 2 + 18;

export type OrgPersonPhotoNodeType = 'org_person_photo' | 'org_person_photo_left' | 'org_person_photo_right';
export type OrgPersonPhotoAnchorSide = 'top' | 'right' | 'bottom' | 'left';

export const isCompactOrgPersonPhotoCard = (cardHeight: number): boolean => cardHeight <= 100;

const resolveIsCompactOrgPersonPhotoCard = (
  cardHeight: number,
  viewMode?: OrgPersonCardViewMode,
): boolean => viewMode ? viewMode === 'compact' : isCompactOrgPersonPhotoCard(cardHeight);

export const getOrgPersonPhotoTopCenterAvatarCenterOffsetY = (
  cardHeight: number,
  viewMode?: OrgPersonCardViewMode,
): number => {
  if (!resolveIsCompactOrgPersonPhotoCard(cardHeight, viewMode)) return 0;
  return -20;
};

export const getOrgPersonPhotoTopCenterAvatarTopOffsetY = (
  cardHeight: number,
  viewMode?: OrgPersonCardViewMode,
): number => (
  getOrgPersonPhotoTopCenterAvatarCenterOffsetY(cardHeight, viewMode) - ORG_PHOTO_CENTER_AVATAR_OUTER_RADIUS
);

export const getOrgPersonPhotoTopCenterTopOverflow = (
  cardHeight: number,
  viewMode?: OrgPersonCardViewMode,
): number => (
  -getOrgPersonPhotoTopCenterAvatarTopOffsetY(cardHeight, viewMode)
);

/**
 * The visual card bounds include the circular avatar above a Photo Top card.
 * Floating UI must use these bounds rather than the persisted card rectangle.
 */
export const getOrgPersonPhotoTopCenterVisualBounds = (
  cardX: number,
  cardY: number,
  cardWidth: number,
  cardHeight: number,
  viewMode?: OrgPersonCardViewMode,
): { x: number; y: number; width: number; height: number } => {
  const topOverflow = getOrgPersonPhotoTopCenterTopOverflow(cardHeight, viewMode);
  return {
    x: cardX,
    y: cardY - topOverflow,
    width: cardWidth,
    height: cardHeight + topOverflow,
  };
};

export const resolveOrgPersonPhotoTopCenterTopAnchor = (
  cardX: number,
  cardY: number,
  cardWidth: number,
  cardHeight: number,
  offset = 0.5,
  viewMode?: OrgPersonCardViewMode,
): { x: number; y: number } => {
  const t = Math.max(0, Math.min(1, offset));
  return {
    x: cardX + cardWidth * t,
    y: cardY + getOrgPersonPhotoTopCenterAvatarTopOffsetY(cardHeight, viewMode),
  };
};

export const expandOrgPersonPhotoTopCenterRoutingRect = (
  cardX: number,
  cardY: number,
  cardWidth: number,
  cardHeight: number,
  viewMode?: OrgPersonCardViewMode,
): { x: number; y: number; width: number; height: number } => {
  const topOverflow = getOrgPersonPhotoTopCenterTopOverflow(cardHeight, viewMode);
  return {
    x: cardX,
    y: cardY - topOverflow,
    width: cardWidth,
    height: cardHeight + topOverflow,
  };
};

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

const resolveCircleTopY = (centerY: number, radius: number, x: number, centerX: number): number | null => {
  const dx = x - centerX;
  if (Math.abs(dx) > radius) return null;
  return centerY - Math.sqrt((radius * radius) - (dx * dx));
};

const resolveCircleSideX = (
  centerX: number,
  radius: number,
  y: number,
  centerY: number,
  side: 'left' | 'right',
): number | null => {
  const dy = y - centerY;
  if (Math.abs(dy) > radius) return null;
  const deltaX = Math.sqrt((radius * radius) - (dy * dy));
  return side === 'left' ? centerX - deltaX : centerX + deltaX;
};

const resolveOrgPersonPhotoAvatarCenter = (
  nodeType: OrgPersonPhotoNodeType,
  cardX: number,
  cardY: number,
  cardWidth: number,
  cardHeight: number,
  viewMode?: OrgPersonCardViewMode,
): { x: number; y: number } => {
  if (nodeType === 'org_person_photo') {
    return {
      x: cardX + cardWidth / 2,
      y: cardY + (resolveIsCompactOrgPersonPhotoCard(cardHeight, viewMode) ? -20 : 0),
    };
  }

  if (nodeType === 'org_person_photo_left') {
    return {
      x: cardX + (resolveIsCompactOrgPersonPhotoCard(cardHeight, viewMode) ? -ORG_PHOTO_SIDE_COMPACT_CENTER_X_OFFSET : ORG_PHOTO_SIDE_FULL_CENTER_X_INSET),
      y: cardY + (resolveIsCompactOrgPersonPhotoCard(cardHeight, viewMode) ? cardHeight / 2 : ORG_PHOTO_SIDE_FULL_CENTER_Y),
    };
  }

  return {
    x: cardX + (resolveIsCompactOrgPersonPhotoCard(cardHeight, viewMode) ? cardWidth + ORG_PHOTO_SIDE_COMPACT_CENTER_X_OFFSET : cardWidth - ORG_PHOTO_SIDE_FULL_CENTER_X_INSET),
    y: cardY + (resolveIsCompactOrgPersonPhotoCard(cardHeight, viewMode) ? cardHeight / 2 : ORG_PHOTO_SIDE_FULL_CENTER_Y),
  };
};

export const resolveOrgPersonPhotoVisibleAnchorPoint = (
  nodeType: OrgPersonPhotoNodeType,
  side: OrgPersonPhotoAnchorSide,
  cardX: number,
  cardY: number,
  cardWidth: number,
  cardHeight: number,
  offset = 0.5,
  viewMode?: OrgPersonCardViewMode,
): { x: number; y: number } => {
  const t = clamp01(offset);
  const rectPoint = (() => {
    switch (side) {
      case 'top':
        return { x: cardX + cardWidth * t, y: cardY };
      case 'right':
        return { x: cardX + cardWidth, y: cardY + cardHeight * t };
      case 'bottom':
        return { x: cardX + cardWidth * t, y: cardY + cardHeight };
      case 'left':
        return { x: cardX, y: cardY + cardHeight * t };
      default:
        return { x: cardX + cardWidth / 2, y: cardY + cardHeight / 2 };
    }
  })();

  const avatarCenter = resolveOrgPersonPhotoAvatarCenter(nodeType, cardX, cardY, cardWidth, cardHeight, viewMode);

  if (side === 'top') {
    if (
      nodeType === 'org_person_photo' ||
      ((nodeType === 'org_person_photo_left' || nodeType === 'org_person_photo_right') && !resolveIsCompactOrgPersonPhotoCard(cardHeight, viewMode))
    ) {
      const topY = resolveCircleTopY(avatarCenter.y, ORG_PHOTO_CENTER_AVATAR_OUTER_RADIUS, rectPoint.x, avatarCenter.x);
      if (topY !== null && topY < rectPoint.y) {
        return { x: rectPoint.x, y: topY };
      }
    }
    return rectPoint;
  }

  if (side === 'left' && nodeType === 'org_person_photo_left' && resolveIsCompactOrgPersonPhotoCard(cardHeight, viewMode)) {
    const leftX = resolveCircleSideX(avatarCenter.x, ORG_PHOTO_CENTER_AVATAR_OUTER_RADIUS, rectPoint.y, avatarCenter.y, 'left');
    if (leftX !== null && leftX < rectPoint.x) {
      return { x: leftX, y: rectPoint.y };
    }
  }

  if (side === 'right' && nodeType === 'org_person_photo_right' && resolveIsCompactOrgPersonPhotoCard(cardHeight, viewMode)) {
    const rightX = resolveCircleSideX(avatarCenter.x, ORG_PHOTO_CENTER_AVATAR_OUTER_RADIUS, rectPoint.y, avatarCenter.y, 'right');
    if (rightX !== null && rightX > rectPoint.x) {
      return { x: rightX, y: rectPoint.y };
    }
  }

  return rectPoint;
};
